import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {BaseItemPickerProps} from '@github-ui/item-picker-base/base-item-picker-props'
import {ItemPicker, type ExtendedItemProps} from '@github-ui/item-picker-base/ItemPicker'
import {useCallback, useMemo, useRef, useState} from 'react'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {createIssueTypePickerItemLeadingVisual, type IssueTypeColor} from './IssueTypePickerItemLeadingVisual'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'

// We want to keep compat with the existing type
// eslint-disable-next-line relay/no-future-added-value
type IssueFieldDataType = 'DATE' | 'NUMBER' | 'SINGLE_SELECT' | 'TEXT' | '%future added value'

type BaseIssueField = {
  readonly dataType: IssueFieldDataType
  readonly id: string
  readonly name: string
}

type PinnedFieldsType =
  | ReadonlyArray<
      | (BaseIssueField & {readonly __typename: 'IssueFieldDate'})
      | (BaseIssueField & {readonly __typename: 'IssueFieldNumber'})
      | (BaseIssueField & {readonly __typename: 'IssueFieldSingleSelect'})
      | (BaseIssueField & {readonly __typename: 'IssueFieldText'})
      | {
          // This will never be '%other', but we need some
          // value in case none of the concrete values match.
          readonly __typename: '%other'
        }
    >
  | null
  | undefined

export type IssueTypePickerIssueType = {
  id: string
  name: string
  description: string | null | undefined
  color: IssueTypeColor
  isEnabled?: boolean
  pinnedFields?: PinnedFieldsType
}

export type IssueTypePickerBaseProps = Omit<BaseItemPickerProps<IssueTypePickerIssueType>, 'filterItems'> & {
  activeIssueType: IssueTypePickerIssueType | null
  issueTypes?: IssueTypePickerIssueType[]
  showUnset?: boolean
  noTypeItem?: ExtendedItemProps<IssueTypePickerIssueType>
  issueTypeToken?: string
}

export const UNSET_ID = 'UNSET'
const DEFAULT_ISSUE_TYPES: IssueTypePickerIssueType[] = []
export function IssueTypePickerBase({
  activeIssueType,
  issueTypes = DEFAULT_ISSUE_TYPES,
  issueTypeToken,
  noTypeItem,
  showUnset = false,
  anchorElement,
  insidePortal,
  isLoading,
  nested,
  onSelectionChange,
  title = LABELS.typesHeader,
  triggerOpen = true,
  width = 'large',
}: IssueTypePickerBaseProps) {
  const [filter, setFilter] = useState('')

  const issueTypeFromToken = useMemo(() => {
    // For types containing spaces, the type token will be surrounded by double quotes, but the types from the picker will be without quotes
    const formattedIssueTypeToken =
      issueTypeToken && issueTypeToken[0] === '"' && issueTypeToken[issueTypeToken.length - 1] === '"'
        ? issueTypeToken.slice(1, -1)
        : issueTypeToken

    return issueTypes.find(issueType => issueType.name === formattedIssueTypeToken)
  }, [issueTypes, issueTypeToken])

  const items = useMemo(() => {
    const repositoryIssueTypes = issueTypes.slice().sort((a, b) => a.name.localeCompare(b.name))
    if (noTypeItem) {
      repositoryIssueTypes.unshift(SPECIAL_VALUES.noTypeData as IssueTypePickerIssueType)
    } else if (showUnset) {
      repositoryIssueTypes.unshift({
        id: UNSET_ID,
        name: 'No issue type',
        isEnabled: true,
        description: '',
        color: 'GRAY',
      } as IssueTypePickerIssueType)
    }
    if (!filter) return repositoryIssueTypes
    return repositoryIssueTypes.filter(l => l.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0)
  }, [issueTypes, filter, showUnset, noTypeItem])

  const filterItems = useCallback((value: string) => {
    setFilter(value)
  }, [])

  const getItemKey = useCallback((issueType: IssueTypePickerIssueType) => issueType.id, [])

  const convertToItemProps = useCallback(
    (issueType: IssueTypePickerIssueType): ExtendedItemProps<IssueTypePickerIssueType> => {
      return {
        id: issueType.id,
        text: issueType.name,
        description: issueType.description ?? '',
        descriptionVariant: 'block',
        leadingVisual: createIssueTypePickerItemLeadingVisual(issueType.color),
        source: issueType,
      }
    },
    [],
  )

  const initialSelectedItems = useMemo(() => {
    if (noTypeItem?.selected) {
      return [SPECIAL_VALUES.noTypeData as IssueTypePickerIssueType]
    }
    if (activeIssueType) return [activeIssueType]
    if (issueTypeFromToken) return [issueTypeFromToken]
    return []
  }, [activeIssueType, issueTypeFromToken, noTypeItem])

  const anchorRef = useRef<HTMLButtonElement>(null)
  const showNoMatchMessage = useMemo(() => items.length === 0, [items.length])

  const responsiveOnNarrowEnabled = isFeatureEnabled('issues_react_select_panel_fullscreen_on_narrow')

  return (
    <ItemPicker
      loading={isLoading}
      items={items}
      initialSelectedItems={initialSelectedItems}
      filterItems={filterItems}
      title={title}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      placeholderText={`Filter types`}
      selectionVariant="single"
      onSelectionChange={onSelectionChange}
      renderAnchor={anchorProps => anchorElement(anchorProps)}
      insidePortal={insidePortal}
      height={'large'}
      width={width}
      nested={nested}
      resultListAriaLabel={'Issue Type results'}
      triggerOpen={triggerOpen}
      keybindingCommandId="item-pickers:open-issue-type"
      selectPanelRef={anchorRef}
      improvedNoMatchAccessibility={showNoMatchMessage}
      noMatchMessage={
        showNoMatchMessage
          ? {
              title: 'No issue types were found',
              variant: 'empty',
              body: 'Try searching with a different query for results.',
            }
          : undefined
      }
      responsiveOnNarrow={responsiveOnNarrowEnabled}
    />
  )
}

try{ IssueTypePickerBase.displayName ||= 'IssueTypePickerBase' } catch {}